import { useCallback, useEffect, useState } from 'react';

import { useSession } from '@/lib/auth/session';

/**
 * 홈 위젯을 어떻게 놓나 — <b>격자형</b>과 <b>자유형</b>(사용자 요청 2026-09-11).
 *
 * <ul>
 *   <li><b>자유형</b>({@code masonry}) — 위젯이 제 높이만큼만 차지하고 빈 곳을 메운다. 지금까지의 기본이다.</li>
 *   <li><b>격자형</b>({@code grid}) — 한 줄의 위젯이 같은 높이로 맞춰진다. 칸이 가지런해 눈이 줄을 따라간다.</li>
 * </ul>
 *
 * <p>배치(어느 위젯을 켜고 어떤 순서·크기로)와 같은 이유로 <b>localStorage 뿐</b>이다 — 기기의 취향이지
 * 계정의 자료가 아니다({@code useHomeWidgets}). 계정별로 나누는 것도 같다.
 */

export type HomeLayout = 'grid' | 'masonry';

const STORAGE_PREFIX = 'bomi.vendor.widget-layout';

const DEFAULT: HomeLayout = 'masonry';

export function useHomeLayout(): { layout: HomeLayout; setLayout: (next: HomeLayout) => void } {
  const session = useSession();
  const userId = session.data?.id ?? null;

  const [layout, setStored] = useState<HomeLayout>(DEFAULT);

  // 계정이 늦게 도착한다(세션 조회). 그때 다시 읽는다 — 안 그러면 다른 사람 취향을 보게 된다.
  useEffect(() => {
    setStored(userId === null ? DEFAULT : read(userId));
  }, [userId]);

  const setLayout = useCallback(
    (next: HomeLayout) => {
      setStored(next);
      if (userId !== null) write(userId, next);
    },
    [userId],
  );

  return { layout, setLayout };
}

function read(userId: string): HomeLayout {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}.${userId}`);
    return raw === 'grid' || raw === 'masonry' ? raw : DEFAULT;
  }
  catch {
    return DEFAULT;
  }
}

function write(userId: string, value: HomeLayout): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}.${userId}`, value);
  }
  catch {
    // 저장이 막힌 환경(사생활 보호 모드). 이번 화면에서는 그대로 동작한다.
  }
}
